// Driver Offboarding: every employee leaving Fleetly, one case each, with a
// dated checklist against named people, final pay, the exit interview and two
// signatures. db/migrations/0610 to 0616, docs/DRIVER-OFFBOARDING.md.
//
// The list puts the cases waiting for the caller's signature first, so Xolile
// opens on what is waiting for him and Yolisa or Luyanda on what is waiting for
// the summary sign off. Final pay is counted down against BCEA section 32(3),
// seven days after the employment ends, because that is the date that matters
// most to the person leaving.
//
// Cases are opened from the driver record ("Offboard this driver"). Opening one
// automatically when a driver record changes to resigned or dismissed is behind
// a switch that is off until Luyanda turns it on; the banner says which.
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, DoorOpen } from 'lucide-react'
import { useGetOffboardingCases } from '../hooks/backend/offboarding'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { CaseDetail } from '../components/offboarding/CaseDetail'
import { ExitReport } from '../components/offboarding/ExitReport'
import {
  STATUS_LABEL, STATUS_TONE, exitTypeLabel, formatDay, payDueText, progress, type CaseList, type CaseSummary,
} from '../utils/offboarding'

type View = 'cases' | 'report'

export default function Offboarding() {
  const { loading: accessLoading, canView } = useCapabilities()
  const list = useGetOffboardingCases<CaseList>()
  const load = list.trigger
  const [params, setParams] = useSearchParams()
  const selected = params.get('case')
  const [view, setView] = useState<View>(params.get('view') === 'report' ? 'report' : 'cases')

  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { if (canView) refresh() }, [canView, refresh])

  const select = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('case', id); else next.delete('case')
    setParams(next, { replace: true })
  }

  if (accessLoading) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Checking access…</div>
  }
  if (!canView) {
    return <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">Driver offboarding is open to Fleetly staff with cockpit access.</div>
  }

  const data = list.data
  const cases = data?.cases ?? []
  const live = cases.filter((c) => ['open', 'awaiting_signoff', 'signed_off'].includes(c.status))
  const finished = cases.filter((c) => !['open', 'awaiting_signoff', 'signed_off'].includes(c.status))
  const waitingOnMe = data ? live.filter((c) =>
    (data.access.can_sign_off && c.status === 'awaiting_signoff') || (data.access.can_approve && c.status === 'signed_off')).length : 0

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <DoorOpen className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Offboarding</h1>
          <p className="text-xs text-muted-foreground">
            One case for every employee who leaves: the dated checklist, final pay, the exit interview, and the sign off.
          </p>
        </div>
        <div className="flex rounded-md border border-border p-0.5 text-xs">
          {(['cases', 'report'] as View[]).map((v) => (
            <button key={v} type="button" onClick={() => setView(v)}
              className={cn('rounded px-3 py-1', view === v ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]' : 'text-muted-foreground hover:text-foreground')}>
              {v === 'cases' ? 'Cases' : 'Why people leave'}
            </button>
          ))}
        </div>
      </div>

      {data && data.auto_open_on_status_change === false && view === 'cases' && (
        <div className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          Cases are opened from the driver record with "Offboard this driver". Changing a driver to resigned or dismissed does not open one yet.
        </div>
      )}
      {data && data.failures.length > 0 && (
        <div className="border-b border-border bg-rose-50 px-4 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
          {data.failures.length} case{data.failures.length === 1 ? '' : 's'} could not be opened automatically: {data.failures.map((f) => `${f.driver ?? 'a driver'} (${f.error})`).join('; ')}. Open them by hand from the driver record.
        </div>
      )}
      {list.error && <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{list.error}</div>}

      {view === 'report' ? (
        <div className="min-h-0 flex-1 overflow-y-auto"><ExitReport /></div>
      ) : (
        <div className="grid min-h-0 flex-1 lg:grid-cols-[360px_1fr]">
          <aside className={cn('min-h-0 overflow-y-auto border-r border-border', selected && 'hidden lg:block')}>
            {list.loading && !data ? (
              <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading cases…</p>
            ) : (
              <>
                {waitingOnMe > 0 && (
                  <p className="border-b border-border bg-[hsl(var(--brand-soft))] px-4 py-2 text-xs font-medium text-[hsl(var(--brand-soft-foreground))]">
                    {waitingOnMe} waiting for your signature
                  </p>
                )}
                <CaseGroup title="Open" cases={live} selected={selected} onSelect={select}
                  empty="No one is leaving right now. Open a case from the driver record when someone does." />
                {finished.length > 0 && <CaseGroup title="Closed or cancelled in the last 90 days" cases={finished} selected={selected} onSelect={select} empty="" />}
              </>
            )}
          </aside>
          <main className={cn('min-h-0', !selected && 'hidden lg:block')}>
            {selected ? (
              <div className="flex h-full flex-col">
                <button type="button" onClick={() => select(null)} className="border-b border-border px-4 py-2 text-left text-xs text-muted-foreground lg:hidden">Back to the list</button>
                <div className="min-h-0 flex-1"><CaseDetail caseId={selected} onChanged={refresh} /></div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">Choose a case from the list.</div>
            )}
          </main>
        </div>
      )}
    </div>
  )
}

function CaseGroup({ title, cases, selected, onSelect, empty }: {
  title: string; cases: CaseSummary[]; selected: string | null; onSelect: (id: string) => void; empty: string
}) {
  return (
    <section>
      <h2 className="border-b border-border bg-muted/30 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {cases.length === 0 ? (
        empty ? <p className="px-4 py-6 text-sm text-muted-foreground">{empty}</p> : null
      ) : (
        <ul className="divide-y divide-border">
          {cases.map((c) => {
            const pay = payDueText(c)
            const p = progress(c)
            return (
              <li key={c.id}>
                <button type="button" onClick={() => onSelect(c.id)}
                  className={cn('w-full space-y-1 px-4 py-3 text-left hover:bg-muted/40', selected === c.id && 'bg-muted/60')}>
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{c.employee_name ?? 'Employee'}</span>
                    <Chip tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Chip>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {c.case_ref} · {exitTypeLabel(c.exit_type)} · last day {formatDay(c.last_working_day)}{c.dates_confirmed ? '' : ' (provisional)'}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Chip tone={pay.tone}>{pay.text}</Chip>
                    {c.items_overdue > 0 && <Chip tone="red">{c.items_overdue} overdue</Chip>}
                    {c.mine_open > 0 && <Chip tone="brand">{c.mine_open} yours</Chip>}
                  </div>
                  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div className="h-full bg-[hsl(var(--brand))]" style={{ width: `${p.pct}%` }} />
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
